import { useEffect, useState } from "react";
import { ArrowDownCircle, ArrowUpCircle, Scale, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell, ReferenceLine } from "recharts";
import { api, tl, signedTl, shortDate, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBusiness } from "../context/BusinessContext";
import { StatCard } from "../components/StatCard";
import { DiffBadge } from "../components/DiffBadge";
import { PageHeader, card } from "../components/PageHeader";

const RANGES = [7, 14, 30];
const tip = { contentStyle: { borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }, formatter: (v) => tl(v) };

function FlowChart({ series }) {
  const data = series.map((d) => ({ name: shortDate(d.date), Girdi: d.income, Çıktı: d.expense }));
  return (
    <div className={card} data-testid="flow-chart">
      <h3 className="font-heading text-lg font-bold text-slate-900">Günlük Girdi / Çıktı</h3>
      <div className="mt-4 h-72">
        <ResponsiveContainer>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" width={60} />
            <Tooltip {...tip} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Girdi" fill="#0891b2" radius={[6, 6, 0, 0]} />
            <Bar dataKey="Çıktı" fill="#1e3a8a" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DiffChart({ series }) {
  const data = series.map((d) => ({ name: shortDate(d.date), Fark: d.difference ?? 0 }));
  return (
    <div className={card} data-testid="discrepancy-chart">
      <h3 className="font-heading text-lg font-bold text-slate-900">Kasa Açık / Fazla</h3>
      <p className="text-xs text-slate-500">Kırmızı: açık · Yeşil: fazla</p>
      <div className="mt-4 h-72">
        <ResponsiveContainer>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" width={60} />
            <Tooltip {...tip} />
            <ReferenceLine y={0} stroke="#94a3b8" />
            <Bar dataKey="Fark" radius={[6, 6, 6, 6]}>
              {data.map((d, i) => (
                <Cell key={i} fill={d.Fark < 0 ? "#e11d48" : "#10b981"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { current } = useBusiness();
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (current) {
      api.get(`/businesses/${current.id}/dashboard`, { params: { days } }).then((r) => setData(r.data));
    }
  }, [current, days]);

  if (!data) return <div className="h-64 animate-pulse rounded-2xl bg-slate-200/60" />;
  const t = data.today;
  const diffTone = t.difference == null ? "default" : t.difference < 0 ? "red" : t.difference > 0 ? "green" : "default";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader title={`Merhaba, ${user.first_name}`} subtitle={`${current?.name} · ${fmtDate(t.date)}`}>
        <div className="flex rounded-xl border border-slate-200 bg-white p-1">
          {RANGES.map((r) => (
            <button key={r} data-testid={`range-${r}-button`} onClick={() => setDays(r)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${days === r ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              {r} gün
            </button>
          ))}
        </div>
      </PageHeader>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="today-income-card" label="Bugün Girdi" value={tl(t.income)} icon={ArrowUpCircle} tone="navy" hint={`Kasa açılış: ${tl(t.opening)}`} />
        <StatCard testId="today-expense-card" label="Bugün Çıktı" value={tl(t.expense)} icon={ArrowDownCircle} />
        <StatCard testId="today-expected-card" label="Beklenen Kasa" value={tl(t.expected_cash)} icon={Wallet} tone="blue" />
        <div data-testid="today-cash-status-card">
          <StatCard label="Kasa Durumu" value={t.difference == null ? "—" : signedTl(t.difference)} icon={Scale} tone={diffTone}
            hint={<DiffBadge diff={t.difference} testId="today-discrepancy-badge" />} />
        </div>
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <FlowChart series={data.series} />
        <DiffChart series={data.series} />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="month-income-card" label="Aylık Ciro" value={tl(data.month.income)} icon={TrendingUp} tone="green" hint="Kasa açılış hariç" />
        <StatCard testId="month-expense-card" label="Aylık Gider" value={tl(data.month.expense)} icon={TrendingDown} />
        <StatCard testId="month-deficit-card" label="Aylık Toplam Açık" value={tl(data.month.deficit)} tone="red" />
        <StatCard testId="month-surplus-card" label="Aylık Toplam Fazla" value={tl(data.month.surplus)} tone="green" />
      </div>
    </div>
  );
}
